import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, Image as ImageIcon, X, Plus } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hook to load image with auth token and convert to blob URL
function useAuthImage(url, token) {
  const [blobUrl, setBlobUrl] = useState(null);
  
  useEffect(() => {
    if (!url || !token) {
      setBlobUrl(null);
      return;
    }
    
    let cancelled = false;
    
    axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    })
    .then(response => {
      if (!cancelled) {
        const objectUrl = URL.createObjectURL(response.data);
        setBlobUrl(objectUrl);
      }
    })
    .catch(() => {
      if (!cancelled) setBlobUrl(null);
    });
    
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [url, token]);
  
  return blobUrl;
}

// Component to display image with auth
function AuthImage({ src, alt, className, token }) {
  const blobUrl = useAuthImage(src, token);
  
  if (!blobUrl) {
    return (
      <div className={`${className} bg-slate-100 flex items-center justify-center`}>
        <ImageIcon className="w-8 h-8 text-slate-300" />
      </div>
    );
  }
  
  return <img src={blobUrl} alt={alt} className={className} />;
}

const GALLERY_LABELS = [
  "Frente",
  "Lateral Esquerda",
  "Lateral Direita",
  "Traseira",
  "Isométrica",
  "Detalhe",
  "Componente",
  "Outro"
];

export function ImageGalleryModal({ open, onClose, onSuccess, sdpId, token, coverImage, galleryImages = [] }) {
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState(null); // 'cover' or 'gallery'
  const [selectedLabel, setSelectedLabel] = useState("Frente");
  const [customLabel, setCustomLabel] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Por favor, selecione uma imagem válida");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagem muito grande. Máximo 5MB.");
      return;
    }

    setSelectedFile(file);
    setUploadType(type);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadType) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      if (uploadType === "cover") {
        await axios.post(`${API_URL}/api/sdp/${sdpId}/cover-image`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" }
        });
        toast.success("Imagem de capa atualizada!");
      } else {
        const label = selectedLabel === "Outro" ? customLabel : selectedLabel;
        formData.append("label", label || "Imagem");
        await axios.post(`${API_URL}/api/sdp/${sdpId}/gallery`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" }
        });
        toast.success("Imagem adicionada à galeria!");
      }
      
      resetForm();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao fazer upload da imagem");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteCover = async () => {
    if (!confirm("Tem certeza que deseja excluir a imagem de capa?")) return;

    try {
      await axios.delete(`${API_URL}/api/sdp/${sdpId}/cover-image`, { headers });
      toast.success("Imagem de capa excluída!");
      onSuccess();
    } catch (error) {
      toast.error("Erro ao excluir imagem");
    }
  };

  const handleDeleteGalleryImage = async (imageId) => {
    if (!confirm("Tem certeza que deseja excluir esta imagem?")) return;

    try {
      await axios.delete(`${API_URL}/api/sdp/${sdpId}/gallery/${imageId}`, { headers });
      toast.success("Imagem excluída da galeria!");
      onSuccess();
    } catch (error) {
      toast.error("Erro ao excluir imagem");
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setUploadType(null);
    setPreviewUrl(null);
    setSelectedLabel("Frente");
    setCustomLabel("");
  };

  const canAddMoreImages = galleryImages.length < 5;

  return (
    <Dialog open={open} onOpenChange={() => { resetForm(); onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-blue-600" />
            Gerenciar Imagens da SDP
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Cover Image Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Imagem de Capa</h3>
              {coverImage && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={handleDeleteCover}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Excluir
                </Button>
              )}
            </div>
            
            {coverImage ? (
              <div className="relative rounded-lg overflow-hidden border border-slate-200">
                <AuthImage
                  src={`${API_URL}/api/sdp/${sdpId}/cover-image`}
                  alt="Capa"
                  className="w-full h-48 object-cover"
                  token={token}
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleFileSelect(e, 'cover')}
                    />
                    <Button variant="secondary" size="sm">
                      <Upload className="w-4 h-4 mr-1" />
                      Substituir
                    </Button>
                  </label>
                </div>
              </div>
            ) : (
              <label className="cursor-pointer block">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e, 'cover')}
                />
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                  <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                  <p className="text-slate-600">Clique para adicionar imagem de capa</p>
                  <p className="text-sm text-slate-400">JPG, PNG, WEBP ou GIF (máx. 5MB)</p>
                </div>
              </label>
            )}
          </div>

          {/* Gallery Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">
                Galeria de Imagens do Produto
                <span className="ml-2 text-sm font-normal text-slate-500">
                  ({galleryImages.length}/5)
                </span>
              </h3>
            </div>

            {galleryImages.length > 0 && (
              <div className="grid grid-cols-3 gap-3">
                {galleryImages.map((img) => (
                  <div key={img.id} className="relative group rounded-lg overflow-hidden border border-slate-200">
                    <AuthImage
                      src={`${API_URL}/api/sdp/${sdpId}/gallery/${img.id}`}
                      alt={img.label}
                      className="w-full h-32 object-cover"
                      token={token}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-white text-sm font-medium truncate">{img.label}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteGalleryImage(img.id)}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {canAddMoreImages && (
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Label>Tipo de imagem:</Label>
                    <select
                      className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={selectedLabel}
                      onChange={(e) => setSelectedLabel(e.target.value)}
                    >
                      {GALLERY_LABELS.map((label) => (
                        <option key={label} value={label}>{label}</option>
                      ))}
                    </select>
                  </div>
                  {selectedLabel === "Outro" && (
                    <div className="flex-1">
                      <Label>Nome personalizado:</Label>
                      <Input
                        value={customLabel}
                        onChange={(e) => setCustomLabel(e.target.value)}
                        placeholder="Ex: Vista superior"
                        className="mt-1"
                      />
                    </div>
                  )}
                </div>

                <label className="cursor-pointer block">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileSelect(e, 'gallery')}
                  />
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                    <Plus className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                    <p className="text-slate-600">Clique para adicionar imagem à galeria</p>
                    <p className="text-sm text-slate-400">JPG, PNG, WEBP ou GIF (máx. 5MB)</p>
                  </div>
                </label>
              </div>
            )}

            {!canAddMoreImages && (
              <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                Limite de 5 imagens na galeria atingido. Exclua uma imagem para adicionar outra.
              </p>
            )}
          </div>

          {/* Preview Section */}
          {previewUrl && (
            <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-slate-700">Preview da imagem selecionada:</h4>
              <img
                src={previewUrl}
                alt="Preview"
                className="max-h-48 mx-auto rounded-lg shadow-sm"
              />
              <p className="text-sm text-slate-500 text-center">
                {uploadType === 'cover' ? 'Imagem de Capa' : `Galeria: ${selectedLabel === 'Outro' ? customLabel : selectedLabel}`}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => { resetForm(); onClose(); }}>
            Fechar
          </Button>
          {previewUrl && (
            <>
              <Button variant="outline" onClick={resetForm}>
                Cancelar
              </Button>
              <Button onClick={handleUpload} disabled={uploading}>
                {uploading ? "Enviando..." : "Enviar Imagem"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
